import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';

/**
 * THE one chip. Every enum value, status, characterization, and party label
 * renders through this component — the old Badge, chip-class helpers,
 * PartyBadge, and hand-rolled pills all collapsed onto it.
 *
 * Soft fill + tone foreground, NO border (the old border-*\/25 alpha borders
 * computed to ~1.37:1 — invisible). Fixed height + leading-none so a tag's
 * height can never be a function of its ancestor's font-size.
 */

export type TagTone =
  | 'community'
  | 'separate'
  | 'mixed'
  | 'unknown'
  | 'good'
  | 'warn'
  | 'danger'
  | 'info'
  | 'client'
  | 'opposing'
  | 'third'
  | 'joint'
  | 'neutral';

export type TagSize = 'sm' | 'md';
export type TagShape = 'square' | 'pill';

export interface TagProps {
  tone?: TagTone;
  /** sm = 18px tall (dense table rows). md = 22px (everywhere else). */
  size?: TagSize;
  /** 'square' (radius-control) for labels. 'pill' ONLY for counts and dots. */
  shape?: TagShape;
  /** Leading status dot in the tone's full-strength color. */
  dot?: boolean;
  /** Leading icon (lucide-react), sized automatically. */
  icon?: LucideIcon;
  /** Native tooltip text. */
  title?: string;
  children: ReactNode;
  className?: string;
}

const TONES: Record<TagTone, string> = {
  community: 'bg-char-community-soft text-char-community',
  separate: 'bg-char-separate-soft text-char-separate',
  mixed: 'bg-char-mixed-soft text-char-mixed',
  unknown: 'bg-cream-dark text-ink-soft',
  good: 'bg-good-soft text-good',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
  client: 'bg-party-client-soft text-party-client',
  opposing: 'bg-party-opposing-soft text-party-opposing',
  third: 'bg-party-third-soft text-party-third',
  joint: 'bg-party-joint-soft text-party-joint',
  neutral: 'bg-cream-dark text-ink-soft',
};

const DOTS: Record<TagTone, string> = {
  community: 'bg-char-community',
  separate: 'bg-char-separate',
  mixed: 'bg-char-mixed',
  unknown: 'bg-ink-faint',
  good: 'bg-good',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  client: 'bg-party-client',
  opposing: 'bg-party-opposing',
  third: 'bg-party-third',
  joint: 'bg-party-joint',
  neutral: 'bg-ink-faint',
};

export default function Tag({
  tone = 'neutral',
  size = 'md',
  shape = 'square',
  dot,
  icon: Icon,
  title,
  children,
  className,
}: TagProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 shrink-0 whitespace-nowrap align-middle font-semibold leading-none',
        size === 'sm' ? 'h-[18px] px-1 text-2xs' : 'h-[22px] px-2 text-xs',
        shape === 'pill' ? 'rounded-full' : 'rounded-[var(--radius-control)]',
        TONES[tone],
        className,
      )}
    >
      {dot && <span aria-hidden="true" className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOTS[tone])} />}
      {Icon && <Icon size={12} strokeWidth={2.25} className="shrink-0" aria-hidden="true" />}
      {children}
    </span>
  );
}
